import { Dialog } from 'primereact/dialog'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import { Button } from 'primereact/button'
import { useMountEffect } from 'primereact/hooks'
import { isEmpty, padStart } from 'lodash'
import { CommonContext } from '../common/context/commonContext'
import { LocationArea, LocationBay, LocationCol, LocationDetail, LocationRow, LocationSaveDialogProps, LocationSector } from '../../types/inventory'
import { Divider } from 'primereact/divider'
import { CommonCode } from '../../types/common'
import { Dropdown } from 'primereact/dropdown'
import AreaAddDialog from './AreaAddDialog'
import SectorAddDialog from './SectorAddDialog'
import { useTranslation } from 'react-i18next'
import { tokens } from 'locales/tokens'

const LocationSaveDialog = (props: LocationSaveDialogProps) => {
    const { $toast, $http, $alert, $codes } = useContext(CommonContext)
    const { t } = useTranslation()
    const [sectorAddVisible, setSectorAddVisible] = useState<boolean>()
    const [areaAddVisible, setAreaAddVisible] = useState<boolean>()
    const [gdTmpTypCd, setGdTmpTypCd] = useState<CommonCode[]>()
    const [lctOptionCd, setLctOptionCd] = useState<CommonCode[]>()
    const [lctTypCd, setLctTypCd] = useState<CommonCode[]>()
    const [locationAreaCd, setLocationAreaCd] = useState<LocationArea[]>()
    const [sectorCd, setSectorCd] = useState<LocationSector[]>()
    const [bayCd, setBayCd] = useState<LocationBay[]>()
    const [rowCd, setRowCd] = useState<LocationRow[]>()
    const [colCd, setColCd] = useState<LocationCol[]>()
    const [orgLocationDetail, setOrgLocationDetail] = useState<LocationDetail>()
    const [locationDetail, setLocationDetail] = useState<LocationDetail>({
        lctSeq: '',
        lctNo: '',
        centerId: '',
        sectorId: '',
        sectorNm: '',
        areaId: '',
        areaNm: '',
        bayId: '',
        rackRowId: '',
        rackColId: '',
        lctTypCd: '',
        lctTypNm: '',
        goodsTmprCd: 'R', /*하드코딩 고정*/
        goodsTmprNm: '실온',
        lctOptionCd: '',
        lctOptionNm: '',
        lctSttCd: '',
        lctSttNm: '',
        updDtFm: '',
        goodsIdCnt: undefined,
        strgRate: undefined,
        chngTxt: '',
        locationAlias:'',
    })

    useEffect(() => {
        $codes(['GD_TMP_TYP', 'LCT_TYP', 'LCT_OPTION'], true).then((result: CommonCode[][]) => {
            const [GD_TMP_TYP, LCT_TYP, LCT_OPTION] = result
            setGdTmpTypCd(GD_TMP_TYP.filter((d) => d.codeId !== ''))
            setLctTypCd(LCT_TYP.filter((d) => d.codeId !== ''))
            setLctOptionCd(LCT_OPTION.filter((d) => d.codeId !== ''))
        })
    }, [$codes, $http])

    useMountEffect(() => {
        if (!props.data) return
        console.log('props.data', props.data)
        // const { shelfs, ...result } = props.data
        setOrgLocationDetail(props.data) //
        setLocationDetail(props.data)
        getAreaSectorCode(false)

        let bayCd: LocationBay[] = []
        for (let i = 1; i < 100; i++) {
            const bay: LocationBay = {
                bayId: 'B' + padStart(i.toString(), 2, '0'),
                bayNm: 'B' + padStart(i.toString(), 2, '0'),
            }
            bayCd.push(bay)
        }
        // console.log("bayCd=", bayCd)
        setBayCd(bayCd)

        let rowCd: LocationRow[] = []
        for (let i = 1; i < 100; i++) {
            const row: LocationRow = {
                rowId: padStart(i.toString(), 2, '0'),
                rowNm: padStart(i.toString(), 2, '0'),
            }
            rowCd.push(row)
        }
        setRowCd(rowCd)
    })

    useEffect(() => {
        const createColCd = (start: number, end: number) => {
            const colCd = []
            for (let i = start; i <= end; i++) {
                colCd.push({
                    colId: i.toString(),
                    colNm: i.toString(),
                })
            }
            return colCd
        }

        const isSameLocationDetail = () => {
            return (
                props.data.centerId === locationDetail.centerId &&
                props.data.areaId === locationDetail.areaId &&
                props.data.sectorId === locationDetail.sectorId &&
                props.data.bayId === locationDetail.bayId &&
                props.data.rackRowId === locationDetail.rackRowId
            )
        }

        const fetchAndSetColCd = async () => {
            // 하나라도 값이 선택되지 않으면 호출하지 않는다.
            try {
                if (
                    !locationDetail.centerId ||
                    !locationDetail.areaId ||
                    !locationDetail.sectorId ||
                    !locationDetail.bayId ||
                    !locationDetail.rackRowId
                ) {
                    return
                }

                const response = await $http().get('/inventory/location/rack-col-ids', {
                    params: {
                        centerId: locationDetail.centerId,
                        areaId: locationDetail.areaId,
                        sectorId: locationDetail.sectorId,
                        bayId: locationDetail.bayId,
                        rackRowId: locationDetail.rackRowId,
                    },
                })

                const usedRackColIds = response

                let colCd = []
                const availableCols = createColCd(1, 5).filter((col) => !usedRackColIds.includes(col.colId))
                setColCd(availableCols)
            } catch (error) {
                console.error('error:', error)
            }
        }

        // 같은 센터, 구역, 섹터, 베이, 렉 인 경우엔 호출하지 않는다.
        if (isSameLocationDetail()) {
            setColCd(createColCd(1, 5))
            return
        }

        fetchAndSetColCd()
    }, [locationDetail])

    const getAreaSectorCode = useCallback(
        async (isRefresh: boolean) => {
            const result: any = await $http().get('/inventory/area-sector-code')
            if (!result && isEmpty(result.list)) {
                $toast({
                    title: t(tokens.common.notification),
                    text: t(tokens.inventory.no_registered_zone_sector_info),
                    icon: 'warn',
                })
                return
            }
            setLocationAreaCd(result.list)
            if (isRefresh) {
                const area = result.list.find((v: LocationArea) => v.areaId === locationDetail.areaId)
                setSectorCd(area.sectors)
            } else {
                const area = result.list.find((v: LocationArea) => v.areaId === props.data.areaId)
                setSectorCd(area.sectors)
            }
        },
        [$http, locationDetail],
    )

    const onClickSave = async () => {
        if (!locationDetail) return
        //console.log(JSON.stringify(orgLocationDetail))
        //console.log(JSON.stringify(locationDetail))
        if (JSON.stringify(orgLocationDetail) === JSON.stringify(locationDetail)) {
            $alert({
                icon: 'info',
                title: t(tokens.inventory.location_management),
                text: t(tokens.inventory.no_location_modification_history),
            })
            return
        }

        if (isEmpty(locationDetail.areaId)) {
            $alert({
                icon: 'info',
                title: t(tokens.inventory.location_management),
                text: t(tokens.inventory.select_location_area),
            })
            return
        }
        if (isEmpty(locationDetail.sectorId)) {
            $alert({
                icon: 'info',
                title: t(tokens.inventory.location_management),
                text: t(tokens.inventory.select_location_sector),
            })
            return
        }
        if (isEmpty(locationDetail.bayId)) {
            $alert({
                icon: 'info',
                title: t(tokens.inventory.location_management),
                text: t(tokens.inventory.select_location_bay),
            })
            return
        }
        if (isEmpty(locationDetail.rackRowId)) {
            $alert({
                icon: 'info',
                title: t(tokens.inventory.location_management),
                text: t(tokens.inventory.select_rack_column_number),
            })
            return
        }
        if (isEmpty(locationDetail.rackColId)) {
            $alert({
                icon: 'info',
                title: t(tokens.inventory.location_management),
                text: t(tokens.inventory.select_rack_tier),
            })
            return
        }
        if (isEmpty(locationDetail.lctTypCd)) {
            $alert({
                icon: 'info',
                title: t(tokens.inventory.location_management),
                text: t(tokens.inventory.select_location_purpose),
            })
            return
        }
        if (isEmpty(locationDetail.lctOptionCd)) {
            $alert({
                icon: 'info',
                title: t(tokens.inventory.location_management),
                text: t(tokens.inventory.select_location_option),
            })
            return
        }
        if (isEmpty(locationDetail.goodsTmprCd)) {
            $alert({
                icon: 'info',
                title: t(tokens.inventory.location_management),
                text: t(tokens.inventory.select_location_temperature),
            })
            return
        }

        const isConfirm = await $alert({
            icon: 'info',
            title: t(tokens.inventory.location_management),
            text: t(tokens.inventory.confirm_location_modification),
            isConfirm: true,
        })

        if (!isConfirm) return

        const param = {
            ...locationDetail,
            chngTxt: setChangeTxt(),
        }
        const result = await $http().put('/inventory/location-detail', param)
        if (result) {
            $toast({ title: t(tokens.common.success), text: t(tokens.inventory.location_modified), icon: 'success' })
            props.onHide(true)
        }
    }

    const setChangeTxt = () => {
        let chngTxt = ''
        if (locationDetail.areaId !== (orgLocationDetail?.areaId || '')) {
            const area = locationAreaCd?.find((v) => v.areaId === locationDetail.areaId)
            chngTxt += `${t(tokens.common.zone)}:` + (area?.areaNm || '') + '(' + locationDetail.areaId + '), '
        }
        if (locationDetail.sectorId !== (orgLocationDetail?.sectorId || '')) {
            const sector = sectorCd?.find((v) => v.sectorId === locationDetail.sectorId)
            chngTxt += `${t(tokens.inventory.sector)}:` + (sector?.sectorNm || '') + '(' + locationDetail.sectorId + '), '
        }
        if (locationDetail.bayId !== (orgLocationDetail?.bayId || '')) {
            chngTxt += `${t(tokens.inventory.bay)}:` + locationDetail.bayId + ', '
        }
        if (locationDetail.rackRowId !== (orgLocationDetail?.rackRowId || '')) {
            chngTxt += `${t(tokens.inventory.rack_row_number)}:` + locationDetail.rackRowId + ', '
        }
        if (locationDetail.rackColId !== (orgLocationDetail?.rackColId || '')) {
            chngTxt += `${t(tokens.inventory.rack_layer_count)}:` + locationDetail.rackColId + ', '
        }

        if (locationDetail.lctTypCd !== (orgLocationDetail?.lctTypCd || '')) {
            const lctTyp = lctTypCd?.find((v) => v.codeId === locationDetail.lctTypCd)
            chngTxt += `${t(tokens.inventory.location_purpose)}:` + (lctTyp?.codeNm || '') + ', '
        }
        if (locationDetail.lctOptionCd !== (orgLocationDetail?.lctOptionCd || '')) {
            const lctOption = lctOptionCd?.find((v) => v.codeId === locationDetail.lctOptionCd)
            chngTxt += `${t(tokens.inventory.location_option)}:` + (lctOption?.codeNm || '') + ', '
        }
        if (locationDetail.goodsTmprCd !== (orgLocationDetail?.goodsTmprCd || '')) {
            const goodsTmpr = gdTmpTypCd?.find((v) => v.codeId === locationDetail.goodsTmprCd)
            chngTxt += `${t(tokens.inventory.location_temperature)}:` + (goodsTmpr?.codeNm || '') + ', '
        }

        return chngTxt
    }

    const onChangeArea = (type: string, value: string) => {
        const area = locationAreaCd?.find((v) => v.areaId === value)

        setLocationDetail({
            ...locationDetail,
            [type]: value,
            areaNm: area?.areaNm || '',
            sectorId: '',
            sectorNm: '',
        })

        if (!area) {
            $toast({ title: t(tokens.common.notification), text: t(tokens.inventory.no_registered_zone_info), icon: 'warn' })
            setSectorCd([])
            return
        }
        if (isEmpty(area.sectors)) {
            $toast({ title: t(tokens.common.notification), text: t(tokens.inventory.no_registered_sector_info), icon: 'warn' })
            setSectorCd([])
            return
        }
        setSectorCd(area.sectors)
    }

    const onChangeLocation = (type: string, value: string) => {
        if (type === 'sectorId') {
            const sector = sectorCd?.find((v) => v.sectorId === value)
            setLocationDetail({
                ...locationDetail,
                [type]: value,
                sectorNm: sector?.sectorNm || '',
            })
        } else {
            setLocationDetail({
                ...locationDetail,
                [type]: value,
            })
        }
    }

    const header = (title: string, rackNo: string) => {
        return (
            <>
                <div className="col-12 flex p-0">{title}</div>
                <div className="col-12 flex p-0 pt-2 text-lg font-semibold rdms-text-deepblue">{rackNo}</div>
            </>
        )
    }
    const addLocationArea = () => {
        setAreaAddVisible(true)
    }
    const addLocationSector = () => {
        setSectorAddVisible(true)
    }

    const onAreaAddVisibleHide = (isChange: boolean) => {
        if (isChange) {
            getAreaSectorCode(true)
        }
        setAreaAddVisible(false)
    }
    const onSectorAddVisibleHide = (isChange: boolean) => {
        if (isChange) {
            getAreaSectorCode(true)
        }
        setSectorAddVisible(false)
    }

    const footer = () => {
        return (
            <>
                <Divider className="my-2" />
                <div className="grid">
                    <div className="col-6 flex pl-0 mb-3">
                        <Button
                            className="w-full p-button-secondary"
                            label={t(tokens.common.close)}
                            size="large"
                            onClick={() => props.onHide(false)}
                        />
                    </div>
                    <div className="col-6 flex pr-0 mb-3">
                        <Button
                            className={`w-full ${props.isUpdate ? 'p-button-green' : 'p-button-success'}`}
                            label={props.isUpdate ? t(tokens.common.save) : t(tokens.common.register)}
                            size="large"
                            onClick={onClickSave}
                        />
                    </div>
                </div>
            </>
        )
    }

    return (
        <Dialog
            header={header(
                props.isUpdate ? t(tokens.inventory.edit_location) : t(tokens.inventory.register_location),
                props.data.bayId + '-' + props.data.rackRowId + '-' + props.data.rackColId,
            )}
            visible={props.visible}
            onHide={() => props.onHide(false)}
            closable={false}
            contentClassName="pb-0"
            footer={footer}
        >
            {locationDetail && (
                <div className="grid">
                    <div className="col-5 flex font-bold text-md py-0 pr-0 pl-1 mt-2 mb-2">{t(tokens.inventory.location_label_code)} :</div>
                    <div className="col-7 flex py-0 px-0 mt-2 mb-2">{locationDetail.lctSeq}</div>
                    <Divider className="my-0" />
                    <div className="col-12 flex rdms-text-primary font-bold mt-2 grid py-0">
                        <div className="col-9">{t(tokens.common.zone)}(Area)</div>
                        <div className="col-3">{t(tokens.inventory.zone_code)}</div>
                    </div>
                    <div className="col-12 flex mt-0 grid py-0">
                        <div className="col-9 pt-0">
                            <Dropdown
                                id="dateType"
                                value={locationDetail.areaId}
                                className="p-inputtext-lg text-sm"
                                onChange={(e) => onChangeArea('areaId', e.value)}
                                options={locationAreaCd}
                                optionLabel="areaNm"
                                optionValue="areaId"
                                // style={{ minWidth: '180px', zIndex: 2 }}
                            />
                            <Button className="p-button-info ml-2 p-button-lg" label={t(tokens.common.add)} size="small" onClick={addLocationArea} />
                        </div>
                        <div className="col-3 pt-2">{locationDetail.areaId}</div>
                    </div>
                    <Divider className="my-2" />
                    <div className="col-12 flex rdms-text-primary font-bold mt-0 grid py-0">
                        <div className="col-9">{t(tokens.inventory.sector)}(Sector)</div>
                        <div className="col-3">{t(tokens.inventory.sector_code)}</div>
                    </div>
                    <div className="col-12 flex mt-0 grid py-0">
                        <div className="col-9 pt-0">
                            <Dropdown
                                id="dateType"
                                value={locationDetail.sectorId}
                                className="p-inputtext-lg text-sm"
                                onChange={(e) => onChangeLocation('sectorId', e.value)}
                                options={sectorCd}
                                optionLabel="sectorNm"
                                optionValue="sectorId"
                                // style={{ minWidth: '180px', zIndex: 2 }}
                            />
                            <Button
                                className="p-button-info ml-2 p-button-lg"
                                label={t(tokens.common.add)}
                                size="small"
                                onClick={addLocationSector}
                            />
                        </div>
                        <div className="col-3 pt-2">{locationDetail.sectorId}</div>
                    </div>
                    <Divider className="my-2" />
                    <div className="col-12 flex rdms-text-primary font-bold mt-0 grid py-0">
                        <div className="col-9">{t(tokens.inventory.bay)}(Bay)</div>
                        <div className="col-3">{t(tokens.inventory.bay_code)}</div>
                    </div>
                    <div className="col-12 flex mt-0 grid py-0">
                        <div className="col-9 pt-0">
                            <Dropdown
                                id="dateType"
                                value={locationDetail.bayId}
                                className="p-inputtext-lg text-sm"
                                onChange={(e) => onChangeLocation('bayId', e.value)}
                                options={bayCd}
                                optionLabel="bayNm"
                                optionValue="bayId"
                                // style={{ minWidth: '180px', zIndex: 2 }}
                            />
                        </div>
                        <div className="col-3 pt-2">{locationDetail.bayId}</div>
                    </div>
                    <Divider className="my-2" />
                    <div className="col-12 flex rdms-text-primary font-bold mt-0 grid py-0">
                        <div className="col-9">{t(tokens.inventory.rack)}(Rack)</div>
                        <div className="col-3">{t(tokens.inventory.rack_code)}</div>
                    </div>
                    <div className="col-12 flex mt-0 grid py-0">
                        <div className="col-3 pt-3 font-semibold">{t(tokens.inventory.row_number)}</div>
                        <div className="col-6 pt-1">
                            <Dropdown
                                id="dateType"
                                value={locationDetail.rackRowId}
                                className="p-inputtext-lg text-sm"
                                onChange={(e) => onChangeLocation('rackRowId', e.value)}
                                options={rowCd}
                                optionLabel="rowNm"
                                optionValue="rowId"
                                // style={{ minWidth: '180px', zIndex: 2 }}
                            />
                        </div>
                        <div className="col-3 pt-3">
                            {locationDetail.rackRowId}-{locationDetail.rackColId}
                        </div>
                    </div>
                    <div className="col-12 flex mt-0 grid py-0">
                        <div className="col-3 pt-3 font-semibold">{t(tokens.inventory.layer_count)}</div>
                        <div className="col-3 pt-1">
                            <Dropdown
                                id="dateType"
                                value={locationDetail.rackColId}
                                className="p-inputtext-lg text-sm"
                                onChange={(e) => onChangeLocation('rackColId', e.value)}
                                options={colCd}
                                optionLabel="colNm"
                                optionValue="colId"
                                // style={{ minWidth: '180px', zIndex: 2 }}
                            />
                        </div>
                        <div className="col-3 pt-3"></div>
                    </div>
                    <Divider className="my-2" />
                    <div className="col-12 flex mt-0 grid p-0">
                        <div className="col-4 px-0">
                            <label className="align-self-center ml-2 mr-1 font-bold">{t(tokens.inventory.location_code)}</label>
                        </div>
                        <div className="col-8 rdms-text-primary font-bold px-0">
                            {locationDetail.areaId}-{locationDetail.sectorId}-{locationDetail.bayId}-{locationDetail.rackRowId}-
                            {locationDetail.rackColId}
                        </div>
                    </div>
                    <div className="col-12 flex mt-0 grid p-0">
                        <div className="col-4 px-0">
                            <label className="align-self-center ml-2 mr-1 font-bold">{t(tokens.inventory.location_address)}</label>
                        </div>
                        <div className="col-8 rdms-text-primary font-bold px-0">
                            {locationDetail.areaNm} &gt; {locationDetail.sectorNm} &gt; {locationDetail.bayId} &gt; {locationDetail.rackRowId}
                            {t(tokens.common.row)}
                            {locationDetail.rackColId}
                            {t(tokens.common.layer)}
                        </div>
                    </div>
                    <Divider className="my-2" />
                    {/*
                    <div className="col-12 flex mt-0 grid py-0 align-items-center">
                        <div className="col-5">
                            <label className="align-self-center ml-2 mr-1 font-bold">{t(tokens.inventory.location_temperature)}</label>
                        </div>
                        <div className="col-7">
                            <Dropdown
                                id="dateType"
                                value={locationDetail.goodsTmprCd}
                                className="p-inputtext-lg text-sm"
                                onChange={(e) => onChangeLocation('goodsTmprCd', e.value)}
                                options={gdTmpTypCd}
                                disabled={(props.data?.goodsIdCnt || 0) > 0}
                                optionLabel="displayNm"
                                optionValue="codeId"
                                // style={{ minWidth: '180px', zIndex: 2 }}
                            />
                        </div>
                    </div>
                    */}
                    <div className="col-12 flex mt-0 grid py-0 align-items-center">
                        <div className="col-5">
                            <label className="align-self-center ml-2 mr-1 font-bold">{t(tokens.inventory.location_option)}</label>
                        </div>
                        <div className="col-7">
                            <Dropdown
                                id="dateType"
                                value={locationDetail.lctOptionCd}
                                className="p-inputtext-lg text-sm"
                                onChange={(e) => onChangeLocation('lctOptionCd', e.value)}
                                options={lctOptionCd}
                                optionLabel="displayNm"
                                optionValue="codeId"
                                // style={{ minWidth: '180px', zIndex: 2 }}
                            />
                        </div>
                    </div>

                    <div className="col-12 flex mt-0 grid py-0 align-items-center">
                        <div className="col-5">
                            <label className="align-self-center ml-2 mr-1 font-bold">{t(tokens.inventory.location_purpose)}</label>
                        </div>
                        <div className="col-7">
                            <Dropdown
                                id="dateType"
                                value={locationDetail.lctTypCd}
                                className="p-inputtext-lg text-sm"
                                onChange={(e) => onChangeLocation('lctTypCd', e.value)}
                                options={lctTypCd}
                                optionLabel="displayNm"
                                optionValue="codeId"
                                // style={{ minWidth: '180px', zIndex: 2 }}
                            />
                        </div>
                    </div>
                </div>
            )}
            {areaAddVisible && <AreaAddDialog visible={areaAddVisible} onHide={(e) => onAreaAddVisibleHide(e)} />}
            {sectorAddVisible && (
                <SectorAddDialog visible={sectorAddVisible} onHide={(e) => onSectorAddVisibleHide(e)} data={{ ...locationDetail, sectorNm: '' }} />
            )}
        </Dialog>
    )
}

export default LocationSaveDialog
